import React, { ReactNode } from 'react'
import { useMutation } from '@tanstack/react-query'
import { InferType } from 'yup'
import { useAppTranslation } from '../../../../hooks/useAppTranslation'
import { ErrorBoundary } from '../../../../components/common/ErrorBoundary'
import { useCurrentSociety } from '../../../../hooks/useCurrentSociety'
import { SocietyInformationForm } from './SocietyInformationForm'
import { societyAboutSchema } from '../../../../forms/schemas/society_about'
import { ToastType } from '../../../../components/common/Toast/toast-type'
import { useToastNotifications } from '../../../../hooks/useToastNotification'
import { NSocieties } from '../../../../interfaces/services/societies.interfaces'
import { ViewBase } from '../../../../components/common/ViewBase'
import { useDocumentTitle } from '../../../../hooks/useDocumentTitle'
import { reverseDocumentTitle } from '../../../../navigation/reverseDocumentTitle'
import { patchSociety } from '../../../../api/societies'
import { i18nSocietyType } from '../../../../i18n'

export const SocietyInformationView = (): ReactNode => {
  const { translate } = useAppTranslation()
  const { setToastNotification } = useToastNotifications()
  const { society, isLoading: isFetchingSociety } = useCurrentSociety()
  useDocumentTitle(
    reverseDocumentTitle('management:information', {
      '{{ societyName }}': society?.name,
    })
  )
  const { mutate: mutatePatchSociety } = useMutation({
    mutationFn: (params: {
      id: string
      body: NSocieties.NPatch.IRequestBody
    }) => {
      return patchSociety({ id: params.id, body: params.body })
    },
    onError: () => {
      setToastNotification(
        ToastType.DANGER,
        translate('flashMessage.changesNotSavedError')
      )
    },
    onSuccess: () => {
      setToastNotification(
        ToastType.SUCCESS,
        translate('flashMessage.changesSaved')
      )
    },
  })
  if (!society) return null

  const onSubmit = async (
    data: InferType<typeof societyAboutSchema>
  ): Promise<void> => {
    const formData: NSocieties.NPatch.IRequestBody = data
    // Never send org nr since we cannot edit it here anyway
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const { organisationNumber, ...rest } = formData
    if (!society?._id) return
    mutatePatchSociety({ id: society?._id, body: rest })
  }

  const onError = (): void => {
    setToastNotification(
      ToastType.DANGER,
      translate(
        'societyCreateUpdateResourceScreens.flashMessage.createResourceFailure'
      )
    )
  }

  const loading = isFetchingSociety

  return (
    <ErrorBoundary>
      <ViewBase
        title={translate('societyInformationView.title', {
          context: i18nSocietyType(society?.societyTypeDisplayName),
        })}
      >
        <SocietyInformationForm
          onError={onError}
          onSubmit={onSubmit}
          loading={loading}
          society={society}
        />
      </ViewBase>
    </ErrorBoundary>
  )
}
